import {
  useQuery,
  type QueryKey,
  type UseQueryOptions,
  type UseQueryResult,
} from '@tanstack/react-query';
import service from '../service';
import type { ContentSlot, ContentSlotSummary } from '@Types/contentSlot';

type DefaultError = unknown;

export const contentSlotQueryKeys = {
  all: ['contentSlots'] as const,
  lists: () => [...contentSlotQueryKeys.all, 'list'] as const,
  list: (appId: string) => [...contentSlotQueryKeys.lists(), appId] as const,
  details: () => [...contentSlotQueryKeys.all, 'detail'] as const,
  detail: (id: string) => [...contentSlotQueryKeys.details(), id] as const,
};

type UseContentSlotsParams = {
  appId?: string;
  enabled?: boolean;
};

/** Slot list for an app — metadata only, no trees. */
export function useContentSlots<
  TData = ContentSlotSummary[],
  TError = DefaultError,
>(
  { appId, enabled = true }: UseContentSlotsParams,
  options?: Omit<
    UseQueryOptions<ContentSlotSummary[], TError, TData, QueryKey>,
    'queryKey' | 'queryFn' | 'enabled'
  >,
): UseQueryResult<TData, TError> {
  return useQuery<ContentSlotSummary[], TError, TData>({
    queryKey: contentSlotQueryKeys.list(appId ?? ''),
    enabled: enabled && Boolean(appId),
    queryFn: async () => service.getContentSlots(appId as string),
    ...(options as object),
  });
}

type UseContentSlotParams = {
  id?: string;
  enabled?: boolean;
};

/** Full slot (draft + published trees) for the editor. */
export function useContentSlot<TData = ContentSlot, TError = DefaultError>(
  { id, enabled = true }: UseContentSlotParams,
  options?: Omit<
    UseQueryOptions<ContentSlot, TError, TData, QueryKey>,
    'queryKey' | 'queryFn' | 'enabled'
  >,
): UseQueryResult<TData, TError> {
  return useQuery<ContentSlot, TError, TData>({
    queryKey: contentSlotQueryKeys.detail(id ?? ''),
    enabled: enabled && Boolean(id),
    queryFn: async () => service.getContentSlot(id as string),
    ...(options as object),
  });
}
